import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Series: Pharmacogenomics — "Contraception & Clot Risk"

export const contraceptionYasmin: Scenario = {
  slug: 'contraception-yasmin',
  series: 'Pharmacogenomics',
  title: "Her pill was common. Her clot risk was not.",
  socialHook:
    "Yasmin, 26, used the combined pill for three years before a blood clot appeared in her leg. The pill did not act alone. A common clotting variant changed her risk calculation, and no one had known to look for it.",
  meta: {
    description:
      "A fictional case study: Yasmin develops a DVT at 26 while on the combined oral contraceptive pill. Her Factor V Leiden variant and the pill each raise clot risk; together they multiply it. A genetics-informed review explains what happened, which contraception options remain safer, and what her family may need to know.",
  },

  persona: {
    name: 'Yasmin',
    age: 26,
    sex: 'Female',
    ancestry: 'Pakistani-British',
    occupation: 'Postgraduate student',
    chiefConcern:
      "Yasmin developed a DVT — a deep vein thrombosis, a blood clot in her leg — six months ago while on the combined oral contraceptive pill. She was hospitalized, anticoagulated for three months, and is now off the pill. Her hematologist told her she has a clotting disorder and should avoid the combined pill. She wants to understand what happened, whether any hormonal contraception is still an option, and what this means for surgery, travel, or pregnancy.",
    familyHistory: 'No known family history of DVT or clotting disorders — though none of her immediate family have been tested.',
  },

  clinicalPicture: {
    symptoms: [
      'DVT (deep vein thrombosis) left calf — diagnosed six months ago, now resolved',
      'Leg swelling and pain at time of clot — now resolved',
      'Anxiety about future contraception options and recurrence risk',
    ],
    labs: [
      {
        name: 'D-dimer (clot breakdown marker — tested at time of DVT)',
        value: 'Elevated (at time of DVT)',
        refRange: 'Normal when no active clotting',
        flag: 'high',
      },
      {
        name: 'Factor V Leiden (rs6025, R506Q)',
        value: 'POSITIVE — heterozygous (one copy)',
        refRange: 'Negative (not detected)',
        flag: 'high',
      },
    ],
    medications: [
      'Combined oral contraceptive pill (COCP) — discontinued after DVT',
      'Low-molecular-weight heparin then warfarin — anticoagulation for 3 months post-DVT, now completed',
      'Currently on no regular medications',
    ],
    supplements: ['No regular supplements'],
    lifestyle: [
      'Postgraduate student — prolonged desk-based study sessions, reduced daily movement',
      'Non-smoker',
      'No significant alcohol use',
      'BMI within normal range',
    ],
  },

  genetics: [
    {
      gene: 'F5 (Factor V)',
      variant: 'Leiden (R506Q)',
      rsid: 'rs6025',
      genotype: 'Heterozygous — one copy of the Leiden variant',
      plainMeaning:
        "Yasmin's clotting system has a weaker off-switch. Most carriers never have a clot, but when the combined pill is added, the risk can multiply instead of simply adding up.",
      technicalDetail:
        "Factor V is a key clotting protein normally inactivated by Protein C once a clot has formed. The Leiden variant (R506Q) makes Factor V resistant to that inactivation. Heterozygous Factor V Leiden — one copy — is the most common inherited thrombophilia in people of European descent, carried by roughly 1 in 20. Alone it raises lifetime DVT risk approximately 3–8× compared to non-carriers, and most carriers never develop a clot. Combined with the oral contraceptive pill (itself a 3–4× DVT risk amplifier), the risks multiply rather than add — producing a combined figure conservatively estimated at 9–25× population baseline.",
    },
    {
      gene: 'MTHFR',
      variant: 'C677T',
      rsid: 'rs1801133',
      genotype: 'Heterozygous — one copy of the T variant',
      plainMeaning:
        "This adds a smaller, modifiable layer to the clotting picture. It was not the main reason for the DVT, but it gives Yasmin one practical lever to review.",
      technicalDetail:
        "MTHFR encodes an enzyme that converts folate into its active form. The C677T variant mildly reduces that conversion, allowing homocysteine to accumulate. Elevated homocysteine damages the inner lining of blood vessels, promoting a pro-clotting state. At heterozygous carriage, this effect is modest — it is not the primary driver of Yasmin's DVT — but it adds a further modifiable layer of thrombotic tendency. Supplementing with L-methylfolate (the active, pre-converted form) rather than standard folic acid, along with B12 and B6, can reduce homocysteine by 20–30% in most carriers within weeks.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "A blood clot at 26 left her with more questions than answers",
      body: "Yasmin had taken the combined oral contraceptive pill for three years with no problems. Then her left calf swelled overnight. Within days she was in the hospital with a confirmed DVT — a blood clot in a vein in her leg. She stopped the pill, took blood thinners for three months, and recovered. But she was left with a label, 'hereditary thrombophilia,' and very little explanation. Could she use any hormonal contraception again? Did future pregnancy need special planning? Her sister was also on the pill — did she need to be tested?",
    },
    {
      type: 'narrative',
      heading: "The clotting brake was the hidden clue",
      body: "When a blood vessel is damaged, the body forms a clot to seal the injury. Once the job is done, a natural braking system helps stop the clot from spreading. Yasmin has a variant in one of the key clotting proteins, and her version resists that brake. About 1 in 20 people carry Factor V Leiden, and most never have a clot. The variant raises risk, but it does not make a clot inevitable. The picture changes when another risk factor lands on top.",
    },
    {
      type: 'reveal',
      heading: "The pill and Factor V Leiden changed the risk together",
      body: "The combined pill raises clot risk on its own. For most women, that increase remains low enough that the pill is a reasonable option. Yasmin's situation was different because Factor V Leiden and the combined pill can multiply risk together. Long desk-based study sessions likely added another small pressure. This was not a moral failure or a mystery illness. It was a risk pattern that became visible only after the clot. Factor V Leiden can be detected with a blood test, though routine screening before the combined pill is not standard in most guidelines.",
    },
    {
      type: 'reveal',
      heading: "The MTHFR finding was smaller, but useful",
      body: "Yasmin also has a common variant that can mildly affect folate processing. If homocysteine rises, it can irritate blood vessel lining and add a small pro-clotting pressure. In her case, Factor V Leiden plus the combined pill was the main event. The MTHFR finding matters because it is modifiable: L-methylfolate, the active form, bypasses the conversion step that standard folic acid depends on.",
    },
    {
      type: 'action',
      heading: "Five things Yasmin can do now",
      items: [
        "Switch to progestin-only contraception. The progestin-only pill (POP, \"mini-pill\"), subdermal implant (Nexplanon), hormonal IUD (Mirena), and Depo-Provera injections do not carry the same DVT risk as the combined pill. She should discuss the best fit with a clinician who is aware of her Factor V Leiden result.",
        "Address the MTHFR finding with the right supplement form: standard folic acid requires the same enzyme that Yasmin's variant mildly impairs. Switching to L-methylfolate (the active, pre-converted form) alongside B12 and B6 bypasses that step and removes one modifiable contributor to her clotting picture.",
        "Tell her first-degree relatives. Factor V Leiden is inherited, so each parent or sibling has a 50% chance of carrying the same variant. A sister currently using, or considering, the combined pill should discuss testing with her clinician.",
        "Disclose Factor V Leiden at surgical, obstetric, or medical visits. Surgery, hospitalization, and prolonged immobility, including long-haul flights, can raise clot risk. When clinicians know her status, they can plan prevention such as compression stockings, blood-thinning injections, or early mobilization when appropriate.",
        "Plan ahead for pregnancy. Factor V Leiden raises pregnancy-related clot risk and has associations with recurrent miscarriage and placental complications in some carriers, though absolute risks vary. When Yasmin is considering pregnancy, a hematologist or obstetric physician should be involved early so prevention decisions can be individualized.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "How the risk stack changed after the clot",
      caption:
        "Yasmin's clot came from risk factors stacking together. Understanding the genetics does not reverse what happened, but it clarifies what to avoid and which options remain reasonable to discuss.",
      baselineLabel: 'Risk on COCP + Factor V Leiden — peak exposure',
      baseline: 25,
      levers: [
        {
          label: 'Switch to progestin-only contraception',
          delta: -17,
          description:
            "Removing the combined pill eliminates its DVT risk contribution. Progestin-only methods (POP, implant, hormonal coil, Depo-Provera) do not significantly raise clot risk in Factor V Leiden carriers and are considered safe alternatives.",
        },
        {
          label: 'Methylfolate supplementation (homocysteine reduction)',
          delta: -3,
          description:
            "L-methylfolate plus B12 and B6 can meaningfully reduce homocysteine in MTHFR C677T carriers. Bringing homocysteine down removes a secondary, modifiable contributor to the pro-clotting state.",
        },
        {
          label: 'Clot prevention for surgery and long-haul travel',
          delta: -3,
          description:
            "For higher-risk situations — surgery, hospitalization, flights over 4 hours — compression stockings and low-molecular-weight heparin can reduce event risk. Disclosing Factor V Leiden status lets clinicians plan ahead.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'contraception-yasmin',
    label: 'Explore your clotting and contraception genetics in Coach',
  },
}
